<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { onBeforeRouteLeave } from 'vue-router'

import HomePageFooter from '../components/HomePageFooter.vue'
import HomePageHeader from '../components/HomePageHeader.vue'
import LandingBackground from '../components/LandingBackground.vue'
import MessageDialog from '../components/MessageDialog.vue'
import TaskSubmitProgressDialog from '../components/TaskSubmitProgressDialog.vue'
import { apiRequest, assertOk, isAuthRequiredError, AUTH_EXPIRED_EVENT, AUTH_EXPIRED_STORAGE_KEY } from '../lib/apiClient'
import { getTaskThumbFromCache, removeTaskThumbCache, setTaskThumbToCache } from '../lib/taskThumbCache'
import type { LandingSectionKey } from '../lib/landingTypes'
import { getUploadUrl, putFile } from '../lib/upload'
import SegmentedSingleSelect from '../workbench/SegmentedSingleSelect.vue'
import TaskStatusTag from '../components/TaskStatusTag.vue'
import { TASK_STATUS, normalizeTaskStatus } from '../lib/taskStatus'
import IconChevronLeft from '../components/icons/IconChevronLeft.vue'
import IconChevronRight from '../components/icons/IconChevronRight.vue'
import IconClose from '../components/icons/IconClose.vue'
import IconDownload from '../components/icons/IconDownload.vue'
import IconPlay from '../components/icons/IconPlay.vue'
import IconTrash from '../components/icons/IconTrash.vue'
import IconRefresh from '../components/icons/IconRefresh.vue'
import { TASK_TYPES } from '../workbench/taskCatalog'
import { estimateTaskMinutes } from '../lib/taskEstimate'
import { brand } from '../config/brand'
import { absoluteUrl, organizationJsonLd, webApplicationJsonLd } from '../lib/structuredData'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import { usePaymentModalStore } from '../stores/paymentModal'
import { useTaskStore } from '../stores/task'
import { useVideoTaskListStore, type VideoRect as ApiVideoRect, type VideoRemoveMethod } from '../stores/videoTasks'

type StartTaskResponse = { task_id?: string; status?: string; credits_required?: number }
type VideoRemoveResponse = { task_id?: string; status?: string; credits_required?: number }
type LandingRecommendation = { icon: string; name: string; desc: string; href: string }

const props = withDefaults(
  defineProps<{
    landingPageId?: string
    landingSeo?: any
    canonicalPath?: string
    breadcrumbName?: string
    howToTitle?: string
    seoTitle?: string
    seoDescription?: string
    benefitsImages?: string[]
    featuresImages?: string[]
    whyChooseImages?: string[]
    enabledSections?: Partial<Record<LandingSectionKey, boolean>>
    embed?: boolean
    demoVideoUrl?: string
    defaultInvitationCode?: string
    recommendations?: LandingRecommendation[]
    hreflang?: Record<string, string>
  }>(),
  {
    landingPageId: '',
    landingSeo: undefined,
    canonicalPath: '/video-subtitle-removal-app/',
    breadcrumbName: '视频去字幕',
    howToTitle: undefined,
    seoTitle: '',
    seoDescription: '',
    benefitsImages: () => [],
    featuresImages: () => [],
    whyChooseImages: () => [],
    enabledSections: () => ({}),
    embed: false,
    demoVideoUrl: 'https://batchwith.oss-cn-hangzhou.aliyuncs.com/demo/watermark-remover-5s.mp4',
    defaultInvitationCode: 'OF3A7K',
    recommendations: () => [],
    hreflang: () => ({}),
  },
)

const auth = useAuthStore()
const taskStore = useTaskStore()
const videoTasks = useVideoTaskListStore()
const loginModal = useLoginModalStore()
const paymentModal = usePaymentModalStore()

const siteUrl = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  'https://www.wuhenai.com'
).replace(/\/+$/, '')
function runtimeBasePath() {
  const v = (import.meta.env.BASE_URL as string | undefined) || '/'
  return v === '/' ? '/' : v.replace(/\/?$/, '/')
}
function withBase(p: string) {
  const s = String(p || '').trim()
  if (!s) return ''
  if (/^https?:\/\//.test(s)) return s
  return `${runtimeBasePath()}${s.replace(/^\//, '')}`
}

function defaultVideoThumbDataUrl() {
  return withBase('/assets/default-video-thumb.svg')
}

const tutorialIconMaskStyle = computed(() => {
  const href = withBase('/assets/tutorial-icon.svg')
  return {
    backgroundColor: 'currentColor',
    WebkitMask: `url("${href}") no-repeat center / contain`,
    mask: `url("${href}") no-repeat center / contain`,
  } as any
})

const sectionEnabled = (key: LandingSectionKey) => props.enabledSections?.[key] !== false
const recommendations = computed(() => {
  const list = Array.isArray(props.recommendations) ? props.recommendations : []
  return list.slice(0, 4)
})

function resolveRecommendHref(href: string) {
  const s = String(href || '').trim()
  if (!s) return ''
  if (s.startsWith('#')) return s
  if (/^https?:\/\//.test(s)) return s
  const path = s.startsWith('/') ? s : `/${s}`
  if (runtimeBasePath() !== '/') return `${siteUrl}${path}`
  return path
}

const defaultLandingSeo = {
  h1: props.breadcrumbName || '视频去字幕',
  howto: [],
  whereTitle: '',
  whereItems: [],
  apiTitle: '',
  apiPoints: [],
  apiLinkText: '',
  apiHref: '',
  featuresTitle: '',
  featuresItems: [],
  whyNeedTitle: '',
  whyNeedItems: [],
  whyTitle: '',
  whyItems: [],
  quoteTitle: '',
  quoteItems: [],
  faqTitle: '常见问题 (FAQ)',
  faq: [],
}
const landingSeo = computed(() => (props.landingSeo ? props.landingSeo : defaultLandingSeo))

const canonicalPath = computed(() => {
  const p = String(props.canonicalPath || '').trim() || '/video-subtitle-removal-app/'
  const v = p.startsWith('/') ? p : `/${p}`
  return v.replace(/\/?$/, '/')
})
const canonical = computed(() => `${siteUrl}${canonicalPath.value}`)

const htmlLang = computed(() => {
  const m = props.hreflang || {}
  const k = Object.keys(m).find((x) => x && x !== 'x-default') || ''
  return k || 'zh-CN'
})
const hreflangLinks = computed(() => {
  const m = props.hreflang || {}
  const out: Array<{ rel: string; hreflang?: string; href: string }> = []
  for (const [lang, path] of Object.entries(m)) {
    const p = String(path || '').trim()
    if (!p) continue
    const href = /^https?:\/\//.test(p) ? p : `${siteUrl}${p.startsWith('/') ? p : `/${p}`}`
    out.push({ rel: 'alternate', hreflang: lang, href })
  }
  return out
})

const isPrimaryLanding = computed(() => {
  if (props.embed) return false
  const id = String(props.landingPageId || '').trim()
  return id === 'video-subtitle-removal'
})

const description = computed(() => {
  const preset = String(props.seoDescription || '').trim()
  if (preset) return preset
  const steps = landingSeo.value.howto || []
  if (steps.length >= 2) return `${steps[0].desc} ${steps[1].desc}`.trim()
  return '上传视频，框选字幕区域并提交处理，即可下载无字幕版本视频。'
})
const pageTitle = computed(() => {
  const preset = String(props.seoTitle || '').trim()
  if (preset) return preset
  return landingSeo.value.h1 ? `${landingSeo.value.h1} - ${brand.productName}` : brand.productName
})
const socialImage = computed(() => absoluteUrl(siteUrl, brand.ogImagePath))

function whyNeedImageSrc(idx: number) {
  const list = Array.isArray(props.benefitsImages) ? props.benefitsImages : []
  const i = Math.max(0, Math.floor(Number(idx || 0)))
  const pick = list.length ? String(list[i] || list[i % list.length] || '').trim() : ''
  if (pick) return withBase(pick)
  const j = (Math.abs(Number(idx || 0)) % 2) + 1
  return withBase(`/assets/why-need-${j}.jpg`)
}

const featureItems = computed(() => {
  const list = Array.isArray(props.featuresImages) ? props.featuresImages : []
  const items = Array.isArray((landingSeo.value as any)?.featuresItems) ? ((landingSeo.value as any).featuresItems as any[]) : []
  return items.map((f: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return f
    return { ...f, image: img }
  })
})

const whyChooseItems = computed(() => {
  const list = Array.isArray(props.whyChooseImages) ? props.whyChooseImages : []
  const items = Array.isArray((landingSeo.value as any)?.whyItems) ? ((landingSeo.value as any).whyItems as any[]) : []
  return items.map((w: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return w
    return { ...w, image: img }
  })
})

const faqJsonLd = computed(() => {
  if (!sectionEnabled('faq')) return ''
  const faq = Array.isArray((landingSeo.value as any).faq) ? ((landingSeo.value as any).faq as any[]) : []
  if (!faq.length) return ''
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((x: any) => ({
      '@type': 'Question',
      name: String(x.question || ''),
      acceptedAnswer: { '@type': 'Answer', text: String(x.answer || '') },
    })),
  })
})

const orgJsonLd = computed(() => {
  return organizationJsonLd(siteUrl, absoluteUrl(siteUrl, brand.logoPath))
})

const webAppJsonLd = computed(() => {
  if (!isPrimaryLanding.value) return ''
  return webApplicationJsonLd({
    siteUrl,
    name: '无痕AI去字幕',
    description: `${brand.productName}是一款智能图片及视频批处理平台，支持视频去字幕、视频去水印等能力。`,
    inLanguage: htmlLang.value,
  })
})

const webPageJsonLd = computed(() => {
  const out: any = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    description: description.value,
    inLanguage: htmlLang.value,
    isPartOf: { '@id': `${siteUrl}#website` },
  }
  if (isPrimaryLanding.value) out.about = { '@id': `${siteUrl}#webapp` }
  return JSON.stringify(out)
})

const breadcrumbJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: brand.productName, item: siteUrl },
      { '@type': 'ListItem', position: 2, name: props.breadcrumbName || '视频去字幕', item: canonical.value },
    ],
  })
})

useHead(() => {
  const links = [{ rel: 'canonical', href: canonical.value }, ...hreflangLinks.value] as any[]
  const scripts: any[] = []
  if (isPrimaryLanding.value) {
    scripts.push({ type: 'application/ld+json', textContent: orgJsonLd.value })
    scripts.push({ type: 'application/ld+json', textContent: webAppJsonLd.value })
  }
  scripts.push({ type: 'application/ld+json', textContent: webPageJsonLd.value })
  scripts.push({ type: 'application/ld+json', textContent: breadcrumbJsonLd.value })
  if (isPrimaryLanding.value && faqJsonLd.value) scripts.push({ type: 'application/ld+json', textContent: faqJsonLd.value })
  return {
    htmlAttrs: { lang: htmlLang.value },
    title: pageTitle.value,
    meta: [
      { name: 'description', content: description.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: pageTitle.value },
      { property: 'og:description', content: description.value },
      { property: 'og:url', content: canonical.value },
      { property: 'og:image', content: socialImage.value },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle.value },
      { name: 'twitter:description', content: description.value },
      { name: 'twitter:image', content: socialImage.value },
    ],
    link: links,
    script: scripts,
  }
})

const TASK_TYPE = TASK_TYPES.removeText_vid
const initDone = ref(false)

const filePicker = ref<HTMLInputElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const previewWrapEl = ref<HTMLElement | null>(null)
const videoPanelEl = ref<HTMLElement | null>(null)
const taskPanelHeight = ref(0)
let videoPanelRO: ResizeObserver | null = null
let taskPollTimer = 0
let taskPollBusy = false

const videoFile = ref<File | null>(null)
const videoUrl = ref('')
const demoPlayed = ref(false)
const isDemoVideo = computed(() => videoUrl.value === props.demoVideoUrl && !videoFile.value)
const isMobileViewport = ref(false)
const videoW = ref(0)
const videoH = ref(0)
const videoDuration = ref(0)
const originObjectKey = ref('')
const uploadBusy = ref(false)

function syncMobileViewport() {
  const w = Number(window.innerWidth || 0) || 0
  isMobileViewport.value = w > 0 && w < 640
}

const method = ref<VideoRemoveMethod>('sel_area')
const methodOptions = [
  { value: 'sel_area', label: '框内消除' },
  { value: 'all_area', label: '全屏消除' },
] as const
type UiRect = { x: number; y: number; w: number; h: number }
const rect = ref<UiRect>({ x: 0, y: 0, w: 0, h: 0 })
const dragging = ref(false)
const resizing = ref<null | 'nw' | 'ne' | 'sw' | 'se'>(null)
const pointerStart = ref({ x: 0, y: 0 })
const rectStart = ref<UiRect>({ x: 0, y: 0, w: 0, h: 0 })

const warnOpen = ref(false)
const warnText = ref('')
function openWarn(msg: string) {
  warnText.value = String(msg || '').trim()
  warnOpen.value = true
}
function closeWarn() {
  warnOpen.value = false
  warnText.value = ''
}

type SubmitStep = 'upload' | 'create' | 'start'
type SubmitStage = 'uploading' | 'creating' | 'starting' | 'error'

const submitOpen = ref(false)
const submitStage = ref<SubmitStage>('uploading')
const submitUploadPct = ref(0)
const submitErrorText = ref('')
const submitErrorStep = ref<SubmitStep>('upload')

function openSubmitDialog() {
  submitOpen.value = true
  submitStage.value = 'uploading'
  submitUploadPct.value = 0
  submitErrorText.value = ''
  submitErrorStep.value = 'upload'
}

function closeSubmitDialog() {
  submitOpen.value = false
}

function setSubmitError(step: SubmitStep, message: string) {
  submitStage.value = 'error'
  submitErrorStep.value = step
  submitErrorText.value = message
}

function handleAuthExpiredDuringSubmit() {
  closeSubmitDialog()
  try {
    auth.clearToken()
  } catch {}
  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })
}

function onAuthExpired() {
  handleAuthExpiredDuringSubmit()
}

type RechargeEntry = 'manual' | 'insufficient'
async function openRechargeModal(entry: RechargeEntry, creditsRequired: number, tipText?: string) {
  if (!ensureLoggedIn()) return
  try {
    await paymentModal.pay({ entry, creditsRequired, tipText })
  } catch {}
}

function ensureLoggedIn(): boolean {
  if (auth.token) return true
  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })
  return false
}

function revokeVideoUrl() {
  if (videoUrl.value && videoUrl.value.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(videoUrl.value)
    } catch {}
  }
  videoUrl.value = ''
}

function resetVideo() {
  revokeVideoUrl()
  videoFile.value = null
  videoW.value = 0
  videoH.value = 0
  videoDuration.value = 0
  originObjectKey.value = ''
  rect.value = { x: 0, y: 0, w: 0, h: 0 }
}

function stopTaskPolling() {
  if (taskPollTimer) {
    try {
      window.clearInterval(taskPollTimer)
    } catch {}
  }
  taskPollTimer = 0
  taskPollBusy = false
  try {
    taskStore.stopPolling()
  } catch {}
}

function hasActiveTasksInList() {
  return videoTasks.items
    .filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
    .some((t: any) => {
    const s = normalizeTaskStatus(t?.status)
    return s === TASK_STATUS.queued || s === TASK_STATUS.processing
    })
}

async function pollTaskDetailsOnce() {
  if (taskPollBusy) return
  if (!auth.token) {
    stopTaskPolling()
    return
  }
  const refreshStatusOnly = async (token: string, taskId: string) => {
    const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token })
    const data = assertOk(resp, '任务查询失败') as any
    const p = Number((data as any)?.progress)
    const progress = Number.isFinite(p) ? Math.max(0, Math.round(p)) : undefined
    videoTasks.upsert({
      task_id: String((data as any)?.task_id || taskId).trim() || taskId,
      status: normalizeTaskStatus((data as any)?.status || 'processing') as any,
      progress,
      preview_url: (data as any)?.preview_url || undefined,
      download_url: (data as any)?.download_url || undefined,
      update_time: Date.now(),
      task_name: String((data as any)?.task_name || '').trim() || undefined,
    } as any)
  }
  const activeIds = videoTasks.items
    .filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
    .filter((t: any) => {
      const s = normalizeTaskStatus(t?.status)
      return s === TASK_STATUS.queued || s === TASK_STATUS.processing
    })
    .map((t: any) => String(t?.task_id || '').trim())
    .filter(Boolean)
  if (!activeIds.length) {
    stopTaskPolling()
    return
  }
  taskPollBusy = true
  try {
    const token = String(auth.token || '').trim()
    if (!token) return
    await Promise.allSettled(activeIds.map((id) => refreshStatusOnly(token, id)))
  } finally {
    taskPollBusy = false
  }
  if (!hasActiveTasksInList()) stopTaskPolling()
}

function startTaskPollingIfNeeded() {
  if (taskPollTimer) return
  if (!hasActiveTasksInList()) return
  taskPollTimer = window.setInterval(() => {
    void pollTaskDetailsOnce()
  }, 15000)
  void pollTaskDetailsOnce()
}

function validateVideoMeta(): '' | 'missing_meta' | 'too_long' | 'too_long_2k' | 'too_large_res' {
  if (!videoW.value || !videoH.value) return 'missing_meta'
  const maxSide = Math.max(videoW.value, videoH.value)
  const minSide = Math.min(videoW.value, videoH.value)

  if (maxSide > 2600 || minSide > 1500) return 'too_large_res'

  const is2k =
    (maxSide > 1968 && maxSide <= 2600) ||
    (minSide > 1088 && minSide <= 1500)

  if (is2k) {
    if (videoDuration.value > 60) return 'too_long_2k'
    return ''
  }

  if (videoDuration.value > 30 * 60) return 'too_long'
  return ''
}

async function handleFileSelected(file: File) {
  if (!file.type.startsWith('video/')) {
    openWarn('不支持的文件类型，请选择视频文件。')
    return
  }
  
  taskStore.setCurrent(null)
  originObjectKey.value = ''
  videoFile.value = file
  revokeVideoUrl()
  videoUrl.value = URL.createObjectURL(file)
}

async function playDemoVideo() {
  if (demoPlayed.value) return
  if (videoFile.value) return
  if (videoUrl.value) return
  demoPlayed.value = true
  videoUrl.value = props.demoVideoUrl
  await nextTick()
  const v = videoEl.value
  if (!v) return
  try {
    v.muted = true
    await v.play()
  } catch {}
}

function onVideoEnded() {
  if (!isDemoVideo.value) return
  const v = videoEl.value
  if (!v) return
  if (!isMobileViewport.value) {
    try {
      v.pause()
      v.currentTime = 0
    } catch {}
    videoUrl.value = ''
    return
  }
  try {
    v.currentTime = 0
    void v.play()
  } catch {}
}

function onPickVideo() {
  if (uploadBusy.value) return
  filePicker.value?.click()
}

function openWeChatMiniProgram() {
  const url = 'weixin://dl/business/?appid=wx34b5be765618750e&path=pages/index/index&env_version=release'
  try {
    window.location.href = url
  } catch {}
}

function openHowTo() {
  const url = 'https://suiyu-network.feishu.cn/wiki/FtpvwylhQiqWNAklbeMcWls1n7b?larkTabName=space#share-OsaDdd1cKo1JX5xeMTMcfiEsnsd'
  try {
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (w) w.opener = null
  } catch {
    window.open(url, '_blank')
  }
}

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement | null
  const f = input?.files?.[0]
  if (f) void handleFileSelected(f)
  if (input) input.value = ''
}

function onDropzoneDragOver(e: DragEvent) {
  e.preventDefault()
}

function onDropzoneDrop(e: DragEvent) {
  e.preventDefault()
  const f = e.dataTransfer?.files?.[0]
  if (f) void handleFileSelected(f)
}

function getPreviewRect() {
  const wrap = previewWrapEl.value
  if (!wrap) return null
  const w = Math.round(wrap.clientWidth)
  const h = Math.round(wrap.clientHeight)
  if (!w || !h) return null
  return { x: 0, y: 0, w, h }
}

function getVideoContentBox() {
  const pr = getPreviewRect()
  if (!pr) return null
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (!vw || !vh) return { x: 0, y: 0, w: pr.w, h: pr.h }
  const videoRatio = vw / vh
  const boxRatio = pr.w / pr.h
  if (videoRatio > boxRatio) {
    const w = pr.w
    const h = Math.max(1, Math.round(pr.w / videoRatio))
    const y = Math.round((pr.h - h) / 2)
    return { x: 0, y, w, h }
  }
  const h = pr.h
  const w = Math.max(1, Math.round(pr.h * videoRatio))
  const x = Math.round((pr.w - w) / 2)
  return { x, y: 0, w, h }
}

function clampRect(r: UiRect, boundsW: number, boundsH: number) {
  const min = 24
  const maxArea = 480000
  let x = Math.round(r.x)
  let y = Math.round(r.y)
  let w = Math.round(r.w)
  let h = Math.round(r.h)
  w = Math.max(min, Math.min(w, boundsW))
  h = Math.max(min, Math.min(h, boundsH))
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (vw > 0 && vh > 0 && boundsW > 0 && boundsH > 0) {
    const scaleX = vw / boundsW
    const scaleY = vh / boundsH
    const areaPx = () => Math.round(w * scaleX) * Math.round(h * scaleY)
    if (areaPx() > maxArea) {
      const areaFloat = Math.max(1, w * h * scaleX * scaleY)
      const k = Math.sqrt(maxArea / areaFloat)
      w = Math.max(min, Math.min(boundsW, Math.floor(w * k)))
      h = Math.max(min, Math.min(boundsH, Math.floor(h * k)))
      for (let i = 0; i < 24 && areaPx() > maxArea; i++) {
        const ow = Math.round(w * scaleX)
        const oh = Math.round(h * scaleY)
        if (ow >= oh) w = Math.max(min, w - 1)
        else h = Math.max(min, h - 1)
      }
    }
  }
  x = Math.max(0, Math.min(x, boundsW - w))
  y = Math.max(0, Math.min(y, boundsH - h))
  return { x, y, w, h }
}

function initRect() {
  const box = getVideoContentBox()
  if (!box) return
  const w = Math.round(box.w * 0.6)
  const h = Math.round(box.h * 0.22)
  const x = Math.round((box.w - w) / 2)
  const y = Math.round(box.h - h - box.h * 0.06)
  rect.value = clampRect({ x, y, w, h }, box.w, box.h)
}

function onRectPointerDown(e: PointerEvent) {
  if (method.value !== 'sel_area') return
  const box = getVideoContentBox()
  if (!box) return
  const target = e.target as HTMLElement | null
  const handle = target?.getAttribute('data-handle') as any
  if (handle === 'nw' || handle === 'ne' || handle === 'sw' || handle === 'se') resizing.value = handle
  else dragging.value = true
  pointerStart.value = { x: e.clientX, y: e.clientY }
  rectStart.value = { ...rect.value }
  try {
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  } catch {}
}

function onRectPointerMove(e: PointerEvent) {
  const box = getVideoContentBox()
  if (!box) return
  if (!dragging.value && !resizing.value) return
  const dx = e.clientX - pointerStart.value.x
  const dy = e.clientY - pointerStart.value.y
  const base = rectStart.value
  let next = { ...base }
  if (dragging.value) {
    next.x = base.x + dx
    next.y = base.y + dy
  } else if (resizing.value) {
    const h = resizing.value
    if (h.includes('e')) next.w = base.w + dx
    if (h.includes('s')) next.h = base.h + dy
    if (h.includes('w')) {
      next.x = base.x + dx
      next.w = base.w - dx
    }
    if (h.includes('n')) {
      next.y = base.y + dy
      next.h = base.h - dy
    }
  }
  rect.value = clampRect(next, box.w, box.h)
}

function onRectPointerUp() {
  dragging.value = false
  resizing.value = null
}

const rectStyle = computed(() => {
  const box = getVideoContentBox()
  const ox = box?.x || 0
  const oy = box?.y || 0
  return {
    transform: `translate(${ox + rect.value.x}px, ${oy + rect.value.y}px)`,
    width: `${rect.value.w}px`,
    height: `${rect.value.h}px`,
  }
})

const rectAtLimit = computed(() => {
  if (method.value !== 'sel_area') return false
  if (!videoFile.value) return false
  const box = getVideoContentBox()
  if (!box) return false
  const boundsW = Math.round(box.w || 0)
  const boundsH = Math.round(box.h || 0)
  if (!boundsW || !boundsH) return false
  const vw = Math.round(videoW.value || 0)
  const vh = Math.round(videoH.value || 0)
  if (!vw || !vh) return false
  const maxArea = 480000
  const scaleX = vw / boundsW
  const scaleY = vh / boundsH
  const w = Math.round(Math.round(rect.value.w) * scaleX)
  const h = Math.round(Math.round(rect.value.h) * scaleY)
  const area = Math.max(0, w) * Math.max(0, h)
  return area >= maxArea - 3600
})

function rectToVideoPixels(): UiRect {
  const box = getVideoContentBox()
  if (!box || !videoW.value || !videoH.value) return { x: 0, y: 0, w: 0, h: 0 }
  const scaleX = videoW.value / box.w
  const scaleY = videoH.value / box.h
  return {
    x: Math.round(rect.value.x * scaleX),
    y: Math.round(rect.value.y * scaleY),
    w: Math.round(rect.value.w * scaleX),
    h: Math.round(rect.value.h * scaleY),
  }
}

function rectToApiPixels(): ApiVideoRect {
  const r = rectToVideoPixels()
  return { x1: r.x, y1: r.y, x2: r.x + r.w, y2: r.y + r.h }
}

async function createThumb(): Promise<string> {
  const vid = videoEl.value
  if (!vid) return ''
  const c = document.createElement('canvas')
  const w = Math.max(1, vid.videoWidth || videoW.value || 1)
  const h = Math.max(1, vid.videoHeight || videoH.value || 1)
  const scale = Math.min(1, 320 / w)
  c.width = Math.round(w * scale)
  c.height = Math.round(h * scale)
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(vid, 0, 0, c.width, c.height)
  return c.toDataURL('image/jpeg', 0.72)
}

function statusKey(st: unknown) {
  return normalizeTaskStatus(st)
}

function taskThumbSrc(t: any) {
  const id = String(t?.task_id || '').trim()
  const cached = getTaskThumbFromCache(id)
  if (cached) return cached
  const remote = String(t?.task_thumb || '').trim()
  if (remote) return remote
  const local = String(t?.thumb_data_url || '').trim()
  if (local && local.startsWith('data:image/')) {
    setTaskThumbToCache(id, local)
    return local
  }
  return ''
}

function pad2(n: number) {
  return String(Math.max(0, Math.floor(Number(n) || 0))).padStart(2, '0')
}

function formatMmssFromSeconds(v: unknown) {
  const n = Math.floor(Number(v))
  if (!Number.isFinite(n) || n < 0) return '00:00'
  const mm = Math.floor(n / 60)
  const ss = n % 60
  const m = String(mm)
  return `${m.length < 2 ? m.padStart(2, '0') : m}:${pad2(ss)}`
}

function formatMdHm(ms: unknown) {
  const n = Math.floor(Number(ms))
  if (!Number.isFinite(n) || n <= 0) return '--'
  const d = new Date(n)
  const mm = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  const hh = pad2(d.getHours())
  const mi = pad2(d.getMinutes())
  return `${mm}-${dd} ${hh}:${mi}`
}

function queueHintText(t: any) {
  const p = Math.max(0, Math.round(Number(t?.progress || 0)) || 0)
  if (p <= 0) return '即将开始处理'
  return `前面还有${p}个任务在处理`
}

function processingHintText(t: any) {
  const p = Math.max(0, Math.round(Number(t?.progress || 0)) || 0)
  if (p > 0) return `处理进度 ${p}%`
  return `预计${estimateTaskMinutes({ taskType: TASK_TYPE, meteringSeconds: t?.metering })}分钟内完成`
}

const canSubmit = computed(() => !!videoFile.value && !uploadBusy.value)
const refreshDisabled = computed(() => !auth.token || uploadBusy.value || videoTasks.refreshing || videoTasks.listing)
const visibleTasks = computed(() => {
  if (!initDone.value) return []
  if (!auth.token) return []
  return videoTasks.items.filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
})
const showPager = computed(() => videoTasks.listTotalPages > 1)
const pageText = computed(() => `${videoTasks.listPageIndex + 1}/${videoTasks.listTotalPages}`)
const canPrevPage = computed(() => videoTasks.listPageIndex > 0)
const canNextPage = computed(() => videoTasks.listPageIndex + 1 < videoTasks.listTotalPages)

const submitPriceText = computed(() => {
  if (method.value === 'sel_area') return '5积分/每30秒视频'
  return '10积分/每30秒视频'
})

async function onVideoLoadedMeta() {
  const vid = videoEl.value
  if (!vid) return
  videoW.value = Math.round(vid.videoWidth || 0)
  videoH.value = Math.round(vid.videoHeight || 0)
  videoDuration.value = Number.isFinite(vid.duration) ? vid.duration : 0
  await nextTick()
  if (method.value === 'sel_area') initRect()
}

async function submit() {

  if (!ensureLoggedIn()) return

  if (!videoFile.value) return
  if (uploadBusy.value) return

  if (method.value !== 'sel_area' && method.value !== 'all_area') method.value = 'sel_area'

  const metaError = validateVideoMeta()
  if (metaError) {
    if (metaError === 'too_long_2k')
      openWarn('2K视频去字幕最大支持的视频时长为1分钟，当前视频已超过1分钟。请将视频分辨率调整到1080P，或将视频时长调整到1分钟以内，并再次提交。')
    else if (metaError === 'too_long') openWarn('视频太长，最长支持30分钟。')
    else if (metaError === 'too_large_res') {
      const w = Math.round(Number(videoW.value || 0))
      const h = Math.round(Number(videoH.value || 0))
      openWarn(
        `视频去字幕支持的最大视频分辨率为2K（最大边不超过2600像素，最小边不超过1500像素）。当前视频的分辨率为 ${w} * ${h}，请调整分辨率后再提交处理。`,
      )
    }
    else openWarn('视频信息读取失败，请重新选择视频后再提交。')
    return
  }

  openSubmitDialog()
  uploadBusy.value = true
  try {
    if (!originObjectKey.value) {
      let upload: any
      try {
        upload = await getUploadUrl(auth.token, videoFile.value)
      } catch (e) {
        if (isAuthRequiredError(e)) {
          return
        }
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }

      submitStage.value = 'uploading'
      submitUploadPct.value = 0
      try {
        await putFile(upload, videoFile.value, (p) => {
          submitUploadPct.value = p.percent
        })
      } catch {
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }
      originObjectKey.value = upload.object_key || ''
    }
    if (!originObjectKey.value) throw new Error('上传成功但缺少 object_key')
    submitUploadPct.value = 100

    submitStage.value = 'creating'
    const payload: any = { source_video: originObjectKey.value, method: method.value === 'sel_area' ? 'sel_area' : 'all_area' }
    const rawName = String(videoFile.value.name || '').trim()
    const taskName = rawName.replace(/\.[^./\\]+$/, '')
    if (taskName) payload.task_name = taskName
    if (payload.method === 'sel_area' && rect.value.w > 0 && rect.value.h > 0) payload.rect = rectToApiPixels()

    let taskId = ''
    let createdStatus = 'created'
    let creditsRequired = 0
    try {
      const resp = await apiRequest<VideoRemoveResponse>('/video/removeText', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      } as any)
      const data = assertOk(resp, '提交失败') as any
      taskId = String(data.task_id || '').trim()
      createdStatus = String(data.status || 'created').toLowerCase()
      creditsRequired = typeof data.credits_required === 'number' ? data.credits_required : 0
      if (!taskId) throw new Error('empty task_id')
    } catch {
      setSubmitError('create', '任务创建失败，请重新提交或联系客服处理。')
      return
    }

    taskStore.setCurrent({ task_id: taskId, status: 'created' } as any)
    videoTasks.upsert({
      task_id: taskId,
      title: videoFile.value.name || '视频去字幕',
      created_at: Date.now(),
      task_type: TASK_TYPE,
      status: createdStatus as any,
      source_video: originObjectKey.value,
      method: payload.method,
      rect: payload.rect,
    } as any)
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, 0, 's', undefined, true, { authExpiredMode: 'silent' })
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }

    submitStage.value = 'starting'
    const startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    const startCode = Number(startResp.code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : creditsRequired
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String(startResp.message || '').trim())
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    {
      const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }

    try {
      const thumb = await createThumb()
      if (thumb) {
        setTaskThumbToCache(taskId, thumb)
        videoTasks.upsert({ task_id: taskId, update_time: Date.now() } as any)
      }
    } catch {}

    void (async () => {
      try {
        await auth.fetchMe()
      } catch {}
    })()
    resetVideo()
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    setSubmitError('create', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

async function refreshTasks() {
  if (!auth.token) return
  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, videoTasks.listPageIndex, 's', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  startTaskPollingIfNeeded()
}

async function gotoPage(nextIndex: number) {
  if (!auth.token) return
  const idx = Math.max(0, Math.min(videoTasks.listTotalPages - 1, Math.round(Number(nextIndex || 0)) || 0))
  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, idx, 's', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  startTaskPollingIfNeeded()
}

function prevPage() {
  if (!canPrevPage.value) return
  void gotoPage(videoTasks.listPageIndex - 1)
}

function nextPage() {
  if (!canNextPage.value) return
  void gotoPage(videoTasks.listPageIndex + 1)
}

async function startExistingTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  openSubmitDialog()
  submitStage.value = 'starting'
  submitUploadPct.value = 100
  uploadBusy.value = true
  try {
    const startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    const startCode = Number(startResp.code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof startResp.data?.credits_required === 'number' ? startResp.data.credits_required : 0
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String(startResp.message || '').trim())
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    {
      const st = statusKey(startResp.data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    setSubmitError('start', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

async function cancelTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  uploadBusy.value = true
  try {
    const resp = await apiRequest<any>('/task/stop', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '取消失败'))
    {
      const st = statusKey((resp as any)?.data?.status) || TASK_STATUS.paused
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
  } catch (e) {
    openWarn(e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

const downloadingTaskId = ref('')
const deletingTaskId = ref('')

function sanitizeFilename(v: unknown) {
  const s = String(v || '').trim()
  const cleaned = s.replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_').replace(/\s+/g, ' ').trim()
  return cleaned || '视频去字幕'
}

function extFromUrl(u: string) {
  try {
    const url = new URL(u, window.location.href)
    const p = url.pathname.split('/').pop() || ''
    const m = p.match(/\.([a-z0-9]{1,5})$/i)
    if (!m) return ''
    return `.${String(m[1] || '').toLowerCase()}`
  } catch {
    return ''
  }
}

function rfc5987Encode(v: string) {
  return encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
}

function withContentDisposition(url: string, filename: string) {
  const href = String(url || '').trim()
  const name = String(filename || '').trim()
  if (!href || !name) return href
  try {
    const u = new URL(href)
    const disp = `attachment; filename*=UTF-8''${rfc5987Encode(name)}`
    u.searchParams.set('response-content-disposition', disp)
    return u.toString()
  } catch {
    return href
  }
}

function triggerBrowserDownload(url: string, filename: string) {
  const href = String(url || '').trim()
  if (!href) return
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

async function downloadUrlAs(url: string, filename: string) {
  const name = sanitizeFilename(filename)
  const ext = extFromUrl(url)
  const finalName = ext && !name.toLowerCase().endsWith(ext) ? `${name}${ext}` : name
  try {
    const href = withContentDisposition(url, finalName)
    triggerBrowserDownload(href, finalName)
  } catch {}
}

async function downloadTask(t: any) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  if (downloadingTaskId.value) return
  const taskId = String(t?.task_id || t || '').trim()
  if (!taskId) return
  const taskName = sanitizeFilename(String(t?.task_name || t?.taskName || t?.title || '视频去字幕'))
  downloadingTaskId.value = taskId
  try {
    const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token: auth.token })
    const data = assertOk(resp, '任务下载存在异常，请联系客服解决。') as any
    const url = String(data?.download_url || '').trim()
    if (!url) throw new Error('missing_download_url')
    videoTasks.upsert({ task_id: taskId, download_url: url } as any)
    await downloadUrlAs(url, taskName)
  } catch (e) {
    if (isAuthRequiredError(e)) return
    openWarn('任务下载存在异常，请联系客服解决。')
  } finally {
    downloadingTaskId.value = ''
  }
}

async function removeTask(taskId: string) {
  if (deletingTaskId.value) return
  if (!auth.token) {
    removeTaskThumbCache(taskId)
    videoTasks.remove(taskId)
    return
  }
  deletingTaskId.value = taskId
  try {
    const resp = await apiRequest<any>('/task/delete', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '删除失败'))
    removeTaskThumbCache(taskId)
    videoTasks.remove(taskId)
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, videoTasks.listPageIndex, 's', undefined, true, { authExpiredMode: 'silent' })
      if (videoTasks.listPageIndex >= videoTasks.listTotalPages) {
        await videoTasks.fetchRecent(
          auth.token,
          1,
          TASK_TYPE,
          Math.max(0, videoTasks.listTotalPages - 1),
          's',
          undefined,
          true,
          { authExpiredMode: 'silent' },
        )
      }
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }
  } catch (e) {
    openWarn(e instanceof Error ? e.message : String(e))
  } finally {
    deletingTaskId.value = ''
  }
}

onMounted(async () => {

  try {
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  } catch {}
  try {
    window.addEventListener('storage', onStorage)
  } catch {}
  try {
    syncMobileViewport()
    window.addEventListener('resize', syncMobileViewport)
  } catch {}
  try {
    taskPanelHeight.value = 0
    await nextTick()
    syncTaskPanelHeight()
    try {
      requestAnimationFrame(() => syncTaskPanelHeight())
    } catch {}
    if (typeof ResizeObserver !== 'undefined') {
      videoPanelRO = new ResizeObserver(() => syncTaskPanelHeight())
      if (videoPanelEl.value) videoPanelRO.observe(videoPanelEl.value)
    }
  } catch {}
  try {
    window.addEventListener('resize', syncTaskPanelHeight)
  } catch {}

  
  initDone.value = false
  videoTasks.init()
  void refreshTasks()
  initDone.value = true
  void playDemoVideo()

})

onBeforeUnmount(() => {
  stopTaskPolling()
  try {
    window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  } catch {}
  try {
    window.removeEventListener('storage', onStorage)
  } catch {}
  try {
    window.removeEventListener('resize', syncMobileViewport)
  } catch {}
  try {
    window.removeEventListener('resize', syncTaskPanelHeight)
  } catch {}
  try {
    if (videoPanelRO) videoPanelRO.disconnect()
    videoPanelRO = null
  } catch {}
  revokeVideoUrl()
})

onBeforeRouteLeave(() => {
  stopTaskPolling()
})

watch(
  () => method.value,
  async (m) => {
    if (m !== 'sel_area' && m !== 'all_area') method.value = 'sel_area'
    if (m === 'sel_area') {
      await nextTick()
      initRect()
    }
  },
)

watch(
  () => taskStore.current,
  (t) => {
    if (!auth.token) return
    if (!t?.task_id) return
    videoTasks.upsert({
      task_id: t.task_id,
      status: String(t.status || '').toLowerCase() as any,
      progress: typeof (t as any).progress === 'number' ? (t as any).progress : undefined,
      preview_url: (t as any).preview_url || undefined,
      download_url: (t as any).download_url || undefined,
      update_time: Date.now(),
      task_name: (t as any).task_name || undefined,
    } as any)
  },
  { deep: true },
)

function onStorage() {
  try {
    const v = localStorage.getItem(AUTH_EXPIRED_STORAGE_KEY)
    if (v) onAuthExpired()
  } catch {}
}

function syncTaskPanelHeight() {
  const el = videoPanelEl.value
  if (!el) {
    taskPanelHeight.value = 0
    return
  }
  const h = Math.round(el.getBoundingClientRect().height || 0)
  if (h <= 0) return
  const minH = props.embed ? 0 : 360
  const viewportH = Math.round((window?.innerHeight || 0) as number) || 0
  const maxH = props.embed ? h : (viewportH ? Math.max(520, Math.round(viewportH * 0.9)) : 920)
  const nextH = Math.max(minH, Math.min(h, maxH))
  if (Math.abs(taskPanelHeight.value - nextH) >= 1) taskPanelHeight.value = nextH
}

</script>

<template>
  <LandingBackground :enabled="!props.embed">
    <div class="min-h-full flex flex-col">
      <HomePageHeader v-if="!props.embed" />
      <main class="flex-1">
      <div v-if="!props.embed" class="container">
        <header class="hero">
          <h1 class="heroTitle">{{ landingSeo.h1 }}</h1>
        </header>
      </div>
      <div :class="props.embed ? 'mx-auto max-w-[1200px] px-3 sm:px-6 py-4 sm:py-6' : 'mx-auto max-w-[1200px] px-6 py-10'" data-embed-root>
        <div class="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 items-start">
          <section ref="videoPanelEl" class="rounded-2xl bg-surface-container-low border border-outline-variant/30 overflow-hidden">
            <div :class="props.embed ? 'hidden sm:flex px-4 sm:px-5 pt-2 pb-1 items-center gap-3' : 'hidden sm:flex px-5 pt-2 pb-1 items-center gap-3'">
              <div class="flex items-center gap-4 min-w-0">
                <button
                  class="inline-flex items-center gap-1.5 h-5 text-xs font-extrabold leading-none text-on-surface-variant hover:text-on-surface hover:underline underline-offset-4"
                  type="button"
                  aria-label="使用教程"
                  @click="openHowTo"
                >
                  <span class="w-4 h-4 block shrink-0" aria-hidden="true" :style="tutorialIconMaskStyle"></span>
                  <span>教程</span>
                </button>
              </div>
              <div class="ml-auto flex items-center gap-2">
                <button
                  class="my-1.5 px-5 py-2.5 rounded-[var(--radius-btn)] bg-accent text-white font-black text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  :disabled="uploadBusy"
                  @click="onPickVideo"
                >
                  打开视频
                </button>
                <input ref="filePicker" class="hidden" type="file" accept="video/*" @change="onFileChange" />
              </div>
            </div>

            <div :class="props.embed ? 'p-4 sm:p-5 pt-3' : 'p-5 pt-3'">
              <div
                ref="previewWrapEl"
                class="relative w-full aspect-[4/3] rounded-2xl border border-white/10 overflow-hidden"
                :style="{ background: 'linear-gradient(135deg, rgba(0, 139, 255, 0.22), rgba(91, 35, 255, 0.18))' }"
                aria-label="视频预览"
                @dragover="onDropzoneDragOver"
                @drop="onDropzoneDrop"
              >
                <div v-if="!videoUrl" :class="props.embed ? 'absolute inset-0 grid place-items-center text-center px-4 sm:px-6' : 'absolute inset-0 grid place-items-center text-center px-6'">
                  <button
                    class="w-full max-w-sm p-6 hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                    type="button"
                    aria-label="上传视频"
                    @click="onPickVideo"
                  >
                    <div class="w-12 h-12 rounded-2xl border border-white/10 grid place-items-center mx-auto mb-4 bg-black/20 text-on-surface">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                        <g transform="translate(0 -0.5)">
                          <path
                            d="M4.5 8.5A2.5 2.5 0 0 1 7 6h7a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 14 18H7a2.5 2.5 0 0 1-2.5-2.5v-7Z"
                            stroke="currentColor"
                            stroke-width="2.2"
                            stroke-linejoin="round"
                          />
                          <path d="M16.5 10.2 20 8v8l-3.5-2.2v-3.6Z" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                          <path d="M10 9.3v5.4l5-2.7-5-2.7Z" fill="currentColor" opacity="0.95" />
                        </g>
                      </svg>
                    </div>
                    <div class="text-sm font-extrabold text-on-surface">
                      <span class="sm:hidden">点击选择视频</span>
                      <span class="hidden sm:inline">将视频拖放到此</span>
                    </div>
                    <div class="mt-2 text-xs text-on-surface-variant">
                      支持MP4和MOV格式，最大分辨率2K。<br />
                      1080P及以下，最大时长30分钟；<br />
                      2K视频，最大时长1分钟。
                    </div>
                  </button>
                </div>

                <template v-else>
                  <video
                    ref="videoEl"
                    class="absolute inset-0 w-full h-full block"
                    style="width: 100%; height: 100%; object-fit: contain"
                    :src="videoUrl"
                    :muted="isDemoVideo"
                    :autoplay="isDemoVideo"
                    :loop="isDemoVideo && isMobileViewport"
                    :controls="!isDemoVideo"
                    :disablepictureinpicture="isDemoVideo"
                    controlslist="nodownload noplaybackrate noremoteplayback"
                    playsinline
                    @loadedmetadata="onVideoLoadedMeta"
                    @ended="onVideoEnded"
                  />
                  <div
                    v-if="method === 'sel_area' && videoFile && rectAtLimit"
                    class="absolute left-0 right-0 top-2 px-3 hidden sm:flex justify-center pointer-events-none"
                  >
                    <div class="px-3 py-1.5 rounded-xl bg-black/55 text-error text-xs font-extrabold">
                      已达到最大框选范围，如仍无法覆盖所有字幕或水印，请使用【全屏消除】模式
                    </div>
                  </div>
                  <div
                    v-if="method === 'sel_area' && videoFile"
                    class="vg-rect-overlay"
                    :class="['hidden sm:block', rectAtLimit ? 'vg-rect-overlay--error' : '']"
                    :style="rectStyle"
                    @pointerdown="onRectPointerDown"
                    @pointermove="onRectPointerMove"
                    @pointerup="onRectPointerUp"
                    @pointercancel="onRectPointerUp"
                  >
                    <span class="vg-rect-handle nw" data-handle="nw" aria-hidden="true"></span>
                    <span class="vg-rect-handle ne" data-handle="ne" aria-hidden="true"></span>
                    <span class="vg-rect-handle sw" data-handle="sw" aria-hidden="true"></span>
                    <span class="vg-rect-handle se" data-handle="se" aria-hidden="true"></span>
                  </div>
                </template>
              </div>

              <div v-if="isDemoVideo" class="mt-4 sm:hidden">
                <button
                  class="mobileMiniAppCta"
                  type="button"
                  @click="openWeChatMiniProgram"
                >
                  开始视频去水印去字幕
                </button>
              </div>

              <div class="mt-6 hidden sm:flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div class="w-full sm:max-w-[220px]">
                  <SegmentedSingleSelect v-model="method" :options="(methodOptions as any)" active-border active-border-color="#008BFF" />
                </div>

                <button
                  class="w-full sm:w-[180px] px-5 py-2.5 rounded-[var(--radius-btn)] bg-highlight text-ink font-black hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  :disabled="!canSubmit"
                  @click="submit"
                >
                  <div v-if="uploadBusy" class="text-base leading-tight">正在提交</div>
                  <div v-else class="flex flex-col items-center leading-tight">
                    <div class="text-base">提交处理</div>
                    <div class="text-xs font-extrabold opacity-80">{{ submitPriceText }}</div>
                  </div>
                </button>
              </div>
            </div>
          </section>

          <section
            class="hidden sm:flex rounded-2xl bg-surface-container-low border border-outline-variant/30 overflow-hidden flex-col h-[420px] sm:h-[520px]"
            :style="taskPanelHeight ? { height: `${taskPanelHeight}px` } : undefined"
          >
            <div class="p-5 flex items-center justify-between gap-3">
              <div class="text-base font-extrabold text-on-surface">任务列表（近1天）</div>
              <div class="flex items-center gap-1">
                <button
                  class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  aria-label="刷新"
                  :disabled="refreshDisabled"
                  @click="refreshTasks"
                >
                  <IconRefresh class="w-5 h-5" />
                </button>

                <div v-if="showPager" class="flex items-center gap-0.5">
                  <button
                    class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                    type="button"
                    aria-label="上一页"
                    :disabled="refreshDisabled || !canPrevPage"
                    @click="prevPage"
                  >
                    <IconChevronLeft class="w-5 h-5" />
                  </button>

                  <div class="text-xs font-extrabold text-on-surface-variant tabular-nums min-w-[40px] text-center">{{ pageText }}</div>

                  <button
                    class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                    type="button"
                    aria-label="下一页"
                    :disabled="refreshDisabled || !canNextPage"
                    @click="nextPage"
                  >
                    <IconChevronRight class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div class="p-5 overflow-y-auto flex-1 min-h-0 wbThumbScroll">
              <div v-if="!visibleTasks.length" class="py-10 text-on-surface-variant text-sm text-center">暂无1天内的任务，提交后会在这里显示。</div>
              <div v-else class="flex flex-col gap-3">
                <div v-for="t in visibleTasks" :key="t.task_id" class="p-4 rounded-xl border border-outline-variant/30 bg-surface-container-high">
                  <div class="flex items-start gap-3">
                    <div class="w-[54px] h-[54px] rounded-[12px] bg-white/8 border border-white/10 overflow-hidden flex-none">
                      <img v-if="taskThumbSrc(t)" :src="taskThumbSrc(t)" class="w-full h-full object-cover block" alt="" />
                      <img v-else :src="defaultVideoThumbDataUrl()" class="w-full h-full object-cover block opacity-90" alt="" />
                    </div>

                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-3">
                        <div class="text-base font-extrabold text-on-surface truncate">{{ t.task_name || t.title }}</div>
                        <div class="flex-none">
                          <TaskStatusTag :status="t.status" />
                        </div>
                      </div>
                      <div class="mt-1 flex items-center gap-0 text-xs text-on-surface-variant justify-start text-left min-w-0 overflow-hidden">
                        <div class="tabular-nums flex-none pr-1">{{ formatMmssFromSeconds(t.metering) }}</div>
                        <div class="flex-none px-1">｜</div>
                        <div class="min-w-0 truncate flex-[0_1_auto]">{{ t.description || '-' }}</div>
                        <div class="flex-none px-1">｜</div>
                        <div class="tabular-nums flex-none">{{ formatMdHm(t.created_at) }}</div>
                      </div>
                      <div class="mt-3 flex items-center gap-2">
                        <template v-if="statusKey(t.status) === TASK_STATUS.created">
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="启动"
                            :disabled="uploadBusy"
                            @click="startExistingTask(t.task_id)"
                          >
                            <IconPlay class="w-[18px] h-[18px]" />
                          </button>
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="删除"
                            :disabled="uploadBusy || deletingTaskId === t.task_id"
                            @click="removeTask(t.task_id)"
                          >
                            <IconTrash class="w-[18px] h-[18px]" />
                          </button>
                        </template>
                        <button
                          v-else-if="statusKey(t.status) === TASK_STATUS.queued"
                          class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                          type="button"
                          aria-label="取消"
                          :disabled="uploadBusy"
                          @click="cancelTask(t.task_id)"
                        >
                          <IconClose class="w-[18px] h-[18px]" />
                        </button>
                        <div v-if="statusKey(t.status) === TASK_STATUS.queued" class="flex-1 text-right text-xs font-extrabold text-on-surface-variant">
                          {{ queueHintText(t) }}
                        </div>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.paused">
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="启动"
                            :disabled="uploadBusy"
                            @click="startExistingTask(t.task_id)"
                          >
                            <IconPlay class="w-[18px] h-[18px]" />
                          </button>
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="删除"
                            :disabled="uploadBusy || deletingTaskId === t.task_id"
                            @click="removeTask(t.task_id)"
                          >
                            <IconTrash class="w-[18px] h-[18px]" />
                          </button>
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.failed">
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="启动"
                            :disabled="uploadBusy"
                            @click="startExistingTask(t.task_id)"
                          >
                            <IconPlay class="w-[18px] h-[18px]" />
                          </button>
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="删除"
                            :disabled="uploadBusy || deletingTaskId === t.task_id"
                            @click="removeTask(t.task_id)"
                          >
                            <IconTrash class="w-[18px] h-[18px]" />
                          </button>
                        </template>
                        <button
                          v-else-if="statusKey(t.status) === TASK_STATUS.success"
                          class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                          type="button"
                          aria-label="下载"
                          :disabled="uploadBusy || downloadingTaskId === t.task_id"
                          @click="downloadTask(t)"
                        >
                          <IconDownload class="w-[18px] h-[18px]" />
                        </button>
                        <button
                          v-if="statusKey(t.status) === TASK_STATUS.success"
                          class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                          type="button"
                          aria-label="删除"
                          :disabled="uploadBusy || deletingTaskId === t.task_id"
                          @click="removeTask(t.task_id)"
                        >
                          <IconTrash class="w-[18px] h-[18px]" />
                        </button>
                        <div v-else-if="statusKey(t.status) === TASK_STATUS.processing" class="flex-1 text-right text-xs font-extrabold text-on-surface-variant">
                          {{ processingHintText(t) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <template v-if="!props.embed">
        <section v-if="recommendations.length" class="section" id="recommend">
          <div class="container">
            <div class="sectionCard">
              <div class="recommendGrid" aria-label="产品推荐">
                <div class="recommendCell" v-for="(r, idx) in recommendations" :key="idx">
                  <div class="recommendIcon" aria-hidden="true">
                    <img class="recommendIconImg" :src="withBase(r.icon)" alt="" loading="lazy" />
                  </div>
                  <div class="recommendBody">
                    <div class="recommendName">{{ r.name }}</div>
                    <div class="recommendDesc">{{ r.desc }}</div>
                    <a class="ghost mini recommendCta" :href="resolveRecommendHref(r.href)">
                      <span>去使用</span>
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('howTo') && landingSeo.howto?.length" class="section" id="howto">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ props.howToTitle || '视频字幕怎么消除？只需3步，一键AI去字幕' }}</h2>
                <div class="howtoGrid" aria-label="使用步骤">
                  <div class="howtoCell" v-for="(s, idx) in landingSeo.howto" :key="idx">
                    <div class="howtoIcon" aria-hidden="true">
                      <svg viewBox="0 0 44 44" fill="none">
                        <circle cx="22" cy="22" r="20" stroke="currentColor" stroke-width="2" opacity="0.22" />
                        <circle cx="22" cy="22" r="18" stroke="currentColor" stroke-width="2" opacity="0.10" />
                        <text x="22" y="28" text-anchor="middle" font-size="18" font-weight="800" fill="currentColor">{{ Number(idx) + 1 }}</text>
                      </svg>
                    </div>
                    <div class="howtoName">{{ s.title }}</div>
                    <div class="howtoDesc">{{ s.desc }}</div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('api') && landingSeo.apiTitle && landingSeo.apiPoints?.length" class="section api-section" id="api">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <div class="code-wrap">
                  <div class="code-pane">
                    <div class="head"><span class="dots"><i></i><i></i><i></i></span> Sample Code</div>
                    <pre># 提交视频去字幕任务
curl -X POST "https://api.wuhenai.com/v2/video_removal" \
  -H "Authorization: Bearer $ACCESS_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "video_url": "https://cdn.example.com/in.mp4",
    "model": "video_removal_std",
    "method": "all_area",
    "upload_url": "https://oss.yourdomain.com/in.mp4",
    "upload_headers": {"Content-Type": "video/mp4"}
  }'

# 查询处理进度
curl "https://api.wuhenai.com/v1/tasks/{task_id}" \
  -H "Authorization: Bearer $ACCESS_TOKEN"</pre>
                  </div>
                  <div class="api-points">
                    <h2>{{ landingSeo.apiTitle }}</h2>
                    <div class="api-point" v-for="(p, idx) in landingSeo.apiPoints.slice(0, 3)" :key="idx">
                      <span class="ico" aria-hidden="true">
                        <svg v-if="idx === 0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></svg>
                        <svg v-else-if="idx === 1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 4v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V7l7-4z"/><path d="M9 12l2 2 4-4"/></svg>
                        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l4 4-4 4M9 7h12M9 15h8"/></svg>
                      </span>
                      <div>
                        <b>{{ p.title }}</b>
                        <p>{{ p.desc }}</p>
                      </div>
                    </div>
                    <a v-if="landingSeo.apiHref" class="cta api-cta" :href="resolveRecommendHref(landingSeo.apiHref)">
                      <span>{{ landingSeo.apiLinkText || '查看 API 文档' }}</span>
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('benefits') && landingSeo.whyNeedTitle && landingSeo.whyNeedItems?.length" class="section" id="benefits">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.whyNeedTitle }}</h2>
                <div class="whyNeedGrid">
                  <div class="whyNeedCard" v-for="(x, idx) in landingSeo.whyNeedItems" :key="idx">
                    <img class="whyNeedImg" :src="whyNeedImageSrc(Number(idx))" :alt="x.title" loading="lazy" />
                    <div class="whyNeedOverlay">
                      <h3>{{ x.title }}</h3>
                      <p>{{ x.desc }}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('whyChoose') && landingSeo.whyTitle && whyChooseItems.length" class="section" id="why-choose">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.whyTitle }}</h2>
                <div class="whyChooseGrid" aria-label="为什么选择">
                  <div class="whyChooseCard" v-for="(w, idx) in whyChooseItems" :key="idx">
                    <img class="whyChooseImg" :src="withBase(w.image)" :alt="w.title" loading="lazy" />
                    <h3>{{ w.title }}</h3>
                    <p>{{ w.desc }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('features') && landingSeo.featuresTitle && featureItems.length" class="section" id="features">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.featuresTitle }}</h2>
                <div class="featuresList" aria-label="功能亮点">
                  <div class="featureRow" v-for="(f, idx) in featureItems" :key="idx">
                    <div class="featureMedia" aria-hidden="true">
                      <img class="featureImg" :src="withBase(f.image)" :alt="f.title" loading="lazy" />
                    </div>
                    <div class="featureBody">
                      <h3>{{ f.title }}</h3>
                      <p>{{ f.desc }}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('testimonials') && landingSeo.quoteTitle && landingSeo.quoteItems?.length" class="section" id="quotes">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.quoteTitle }}</h2>
                <div class="letterGrid" aria-label="用户推荐信">
                  <article class="letterCard" v-for="(q, idx) in landingSeo.quoteItems.slice(0, 4)" :key="idx">
                    <h3 class="letterTitle">{{ q.title }}</h3>
                    <div class="letterStars" aria-hidden="true">★★★★★</div>
                    <p class="letterText">{{ q.text }}</p>
                    <footer class="letterPerson" aria-label="推荐人信息">
                      <img class="letterAvatar" :src="withBase(`/assets/quote-avatar-${Number(idx) + 1}.jpg`)" :alt="`${q.name} 头像`" loading="lazy" />
                      <div>
                        <div class="letterName">{{ q.name }}</div>
                        <div class="letterJob">{{ q.job }}</div>
                      </div>
                    </footer>
                  </article>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('faq') && landingSeo.faq?.length" class="section faq" id="faq">
          <div class="container">
            <div class="sec-head">
              <h2>{{ landingSeo.faqTitle || '常见问题' }}</h2>
            </div>
            <div aria-label="常见问题">
              <details v-for="(f, idx) in landingSeo.faq" :key="idx">
                <summary>
                  <span>{{ f.question }}</span>
                  <span class="plus">+</span>
                </summary>
                <div class="a">{{ f.answer }}</div>
              </details>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('contact')" class="section" id="contact">
          <div class="container" id="cta">
            <div class="cta-band">
              <h2>欢迎使用无痕AI{{ props.breadcrumbName }}</h2>
              <p>新用户赠送免费试用积分，有问题可随时联系客服</p>
              <a class="btn btn-primary" href="#">开始在线{{ props.breadcrumbName }}｜免费试用
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </a>
              <div class="cta-qr">
                <div class="qr-wrap" aria-label="客服微信二维码">
                  <img :src="withBase('/assets/custom-service-qr.webp')" alt="客服微信二维码" loading="lazy" />
                </div>
                <span class="qr-tag">在线时间：09:00 - 22:00</span>
              </div>
            </div>
          </div>
        </section>
      </template>
    </main>

    <HomePageFooter v-if="!props.embed" />

    <TaskSubmitProgressDialog
      v-model:open="submitOpen"
      :stage="submitStage"
      :upload-pct="submitUploadPct"
      :error-step="submitErrorStep"
      :error-text="submitErrorText"
      aria-label="任务提交中"
      @ack="closeSubmitDialog"
    />

      <MessageDialog v-model:open="warnOpen" title="提示" :message="warnText" variant="info" primary-text="我知道了" @primary="closeWarn" @close="closeWarn" />
    </div>
  </LandingBackground>
</template>

<style src="../styles/videoRectOverlay.css"></style>
<style scoped>
.mobileMiniAppCta {
  width: 100%;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #E4FF30;
  color: rgba(54, 47, 79, 0.98);
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 180ms ease, border-color 180ms ease, transform 180ms ease, filter 180ms ease;
}

.mobileMiniAppCta:hover {
  filter: brightness(0.94);
}

.api-section .code-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0;
  min-height: 360px;
  border: 1px solid var(--line-strong, rgba(255, 255, 255, 0.18));
  border-radius: var(--radius, 16px);
  overflow: hidden;
  background: var(--bg-1, rgba(255, 255, 255, 0.1));
  box-shadow: var(--shadow-card, 0 12px 28px rgba(0, 0, 0, 0.28));
}

.api-section .code-pane {
  padding: 0;
  background: rgba(0, 0, 0, 0.42);
  min-width: 0;
}

.api-section .code-pane .head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.api-section .code-pane .head .dots {
  display: flex;
  gap: 5px;
  margin-right: 8px;
}

.api-section .code-pane .head .dots i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.api-section .code-pane .head .dots i:nth-child(1) {
  background: #ff5a5a;
}

.api-section .code-pane .head .dots i:nth-child(2) {
  background: #f4d35e;
}

.api-section .code-pane .head .dots i:nth-child(3) {
  background: #4ade80;
}

.api-section .code-pane pre {
  padding: 22px 24px;
  overflow-x: hidden;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace);
  font-size: 13px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.86);
  margin: 0;
}

.api-section .code-pane pre .c {
  color: rgba(255, 255, 255, 0.58);
}

.api-section .code-pane pre .k {
  color: var(--highlight, #e4ff30);
}

.api-section .code-pane pre .s {
  color: rgba(136, 214, 255, 0.96);
}

.api-section .code-pane pre .f {
  color: rgba(196, 181, 253, 0.96);
}

.api-section .api-points {
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  min-width: 0;
}

.api-section .api-points h2 {
  font-size: var(--seo-h2-size, 32px);
  font-weight: var(--seo-h2-weight, 700);
  margin: 0 0 18px;
  text-align: left;
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.api-section .api-point .ico {
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: var(--accent-dim, rgba(0, 139, 255, 0.22));
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point .ico svg {
  width: 17px;
  height: 17px;
}

.api-section .api-point b {
  font-size: 15px;
  display: block;
  color: rgba(255, 255, 255, 0.92);
}

.api-section .api-point p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  margin-bottom: 0;
}

.api-section .api-cta {
  align-self: flex-start;
  text-decoration: none;
  border-radius: var(--radius-sm, 12px);
  padding: 12px 18px;
}

.api-section .api-cta svg {
  width: 18px;
  height: 18px;
}

@media (max-width: 960px) {
  .api-section .code-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
